import Vue from "vue";
import axios from "axios";
import utils from "@/utils/utils";
import { getKeycloak } from "../../services/keycloakService";

const state = () => ({
  latestSeries: [],
  userSeries: [],
  userSeriesTree: [],
  series: {},
  serieByVideo: null,
  my_series: [],
  selected_serie: null,
});

const getters = {
  latestSeries: (state) => JSON.parse(JSON.stringify(state.latestSeries)),
  userSeries: (state) => JSON.parse(JSON.stringify(state.userSeries)),
  userSeriesTree: (state) => JSON.parse(JSON.stringify(state.userSeriesTree)),
  series: (state) => JSON.parse(JSON.stringify(state.series)),
  serieByVideo: (state) => JSON.parse(JSON.stringify(state.serieByVideo)),
  my_series: (state) => JSON.parse(JSON.stringify(state.my_series)),
  selected_serie: (state) => JSON.parse(JSON.stringify(state.selected_serie)),
};

const actions = {
  fetchLatestSeries({ commit }) {
    axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/latest`)
      .then((response) => {
        commit("setLatestSeries", response.data);
      });
  },
  fetchUserSeries({ commit }, username) {
    if (typeof username !== "string" || !username.trim()) return;

    axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/user/${username}`)
      .then((response) => {
        commit(
          "setUserSeries",
          response.data?.filter((serie) => !serie?.draft)
        );
      });
  },
  fetchUserSeriesTree({ commit }, username) {
    if (typeof username !== "string" || !username.trim()) return;

    axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/user/${username}/tree`)
      .then((response) => {
        commit("setUserSeriesTree", response.data);
      })
      .catch(() => undefined);
  },
  fetchSeriesBySlug({ commit }, slug) {
    return axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/${encodeURIComponent(slug)}`)
      .then((response) => {
        commit("setSeries", response.data);
        return response.data;
      });
  },
  fetchSerieByVideo({ commit }, videoId) {
    commit("setSerieByVideo", null);
    axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/video/${videoId}`)
      .then((response) => {
        commit("setSerieByVideo", response.data);
      });
  },
  async fetchMySeries({ commit }, username) {
    if (typeof username !== "string" || !username.trim()) return;

    await axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/user/${username}`)
      .then((response) => {
        commit("setMySeries", response.data);
      });
  },
  async setSelectedSerie({ commit }, serie) {
    if (serie) {
      await axios
        .get(`${Vue.prototype.$config.apiUrl}/serie/${encodeURIComponent(serie.slug)}`)
        .then((response) => {
          commit("setSelectedSerie", response.data);
        })
        .catch(() => {
          commit("setSelectedSerie", null);
        });
    } else {
      commit("setSelectedSerie", serie);
    }
    commit("seasons/setSelectedSeason", null, { root: true });
  },
  refreshSelectedSerie({ getters, commit, dispatch, rootGetters }) {
    if (!getters.selected_serie) return;
    axios
      .get(`${Vue.prototype.$config.apiUrl}/serie/${encodeURIComponent(getters.selected_serie.slug)}`)
      .then((response) => {
        commit("setSelectedSerie", response.data);
        // update selected season
        const selected_season = response.data?.seasonsWithVideos?.find(
          (season) => season.id === rootGetters["seasons/selected_season"]?.id
        );
        dispatch(
          "seasons/setSelectedSeason",
          selected_season === undefined
            ? null
            : JSON.parse(JSON.stringify(selected_season)),
          { root: true }
        );
      });
  },
  async createSerie({ getters, dispatch, rootGetters }, serie) {
    let serieId = null;
    await axios
      .post(`${Vue.prototype.$config.apiUrl}/serie`, serie)
      .then(async (response) => {
        serieId = response.data;
        const username = utils.isAdmin()
          ? rootGetters["admin/impersonated_user"]
          : getKeycloak()?.tokenParsed?.preferred_username;
        if (typeof username === "string" && username.trim()) {
          await dispatch("fetchMySeries", username);
        }
      });
    return getters.my_series.find((serie) => serie.id === serieId);
  },
  updateSerie({ dispatch, rootGetters }, serie) {
    axios.put(`${Vue.prototype.$config.apiUrl}/serie`, serie).then(() => {
      const username = utils.isAdmin()
        ? rootGetters["admin/impersonated_user"]
        : getKeycloak()?.tokenParsed?.preferred_username;
      if (typeof username === "string" && username.trim()) {
        dispatch("fetchMySeries", username);
      }
    });
  },
  deleteSerie({ commit, dispatch, rootGetters }, serieId) {
    axios
      .delete(`${Vue.prototype.$config.apiUrl}/serie/${serieId}`)
      .then(() => {
        const username = utils.isAdmin()
          ? rootGetters["admin/impersonated_user"]
          : getKeycloak()?.tokenParsed?.preferred_username;
        if (typeof username === "string" && username.trim()) {
          return dispatch("fetchMySeries", username);
        }
      })
      .then(() => commit("setSelectedSerie", null));
  },
  updateSerieImage({ commit }, serieImageData) {
    return axios
      .put(`${Vue.prototype.$config.apiUrl}/serie/image`, serieImageData)
      .then(() => commit("forceRefreshImage", serieImageData.id));
  },
};

const mutations = {
  setLatestSeries(state, latestSeries) {
    state.latestSeries = JSON.parse(JSON.stringify(latestSeries));
  },
  setUserSeries(state, userSeries) {
    state.userSeries = JSON.parse(JSON.stringify(userSeries));
  },
  setUserSeriesTree(state, userSeriesTree) {
    state.userSeriesTree = JSON.parse(JSON.stringify(userSeriesTree));
  },
  setSeries(state, series) {
    state.series = JSON.parse(JSON.stringify(series));
  },
  setSerieByVideo(state, serieByVideo) {
    state.serieByVideo = JSON.parse(JSON.stringify(serieByVideo));
  },
  setMySeries(state, my_series) {
    state.my_series = JSON.parse(JSON.stringify(my_series));
  },
  setSelectedSerie(state, serie) {
    state.selected_serie = JSON.parse(JSON.stringify(serie));
  },
  forceRefreshImage(state, serieId) {
    state.my_series.find(
      (my_serie) => my_serie.id === serieId
    ).image_url += `?refresh=${Date.now()}`;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
